// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the overlays stacked over a dataset, on both the browser globe
 * (earthTileLayer's map-layer passes) and the VR/AR globe (the overlay
 * shells).
 *
 * There is no basemap to choose (owner, 2026-10-12): under the dataset is
 * always the globe's own Earth — the NASA GIBS Blue Marble tiles on the
 * browser globe, the Blue Marble diffuse on the VR/AR globe — and
 * see-through data shows it. The catalog's basemaps are left out of the
 * list (`fetchLayerCatalog`), and a basemap named by an older Khipu, link
 * or room is ignored.
 *
 * The layers come from the signed AYNI catalog through `/api/layers` (its
 * `builtin-*` overlays) and from the stream host. Which ones a dataset
 * gets by default follows its coverage:
 *
 *   - A streamed dataset with no transparency gets country borders on
 *     top, drawn white, so a full-cover field (a temperature map, a
 *     projection) still says where it is. The owner's rule (2026-09-30,
 *     the same in the desktop apps): only streams, never a catalog picture
 *     or a plain video; "no transparency" is what the shader does — an
 *     opaque stream, or a transparent one whose first frame has no
 *     see-through texel beyond a 0.1 % tolerance (`measureCoverage`).
 *
 * A visitor can change them from the layer picker. A choice about borders
 * stays across datasets, because only the automatic switch-on is undone
 * (`withBordersChoice`).
 */

import type { MapLayerTint } from './earthTileLayer'
import { colorKeyAlpha, type ColorKey } from './colorKey'
import { streamHostBase } from './streamHost'

export type { MapLayerTint }

export interface CatalogLayer {
  readonly id: string
  readonly title: string
  readonly kind: 'basemap' | 'overlay'
  readonly url: string
  /** AYNI: a small picture for the picker, when the catalog has one (the NOAA overlays). */
  readonly thumb?: string
  /** AYNI: how the catalog says to draw it (`source`: its own colours, as the NOAA overlays). */
  readonly tint?: MapLayerTint
}

export interface LayerSelection {
  readonly overlays: ReadonlyArray<{ readonly id: string; readonly tint: MapLayerTint }>
}

export const NO_LAYERS: LayerSelection = { overlays: [] }

/**
 * AYNI (owner, 2026-10-09): overlays on at once, in every environment
 * (the browser, the headset, phone AR, Khipus and lessons); one until then.
 */
export const MAX_OVERLAYS = 2

/**
 * The overlays after one is pressed in a list: on if it was off (in `tint`),
 * off if it was on. Past `MAX_OVERLAYS` the first one added gives way, as
 * the real-time streams do. Drawn in this order, the last on top.
 */
export function toggleOverlay(
  overlays: LayerSelection['overlays'],
  id: string,
  tint: MapLayerTint,
): LayerSelection['overlays'] {
  if (overlays.some(o => o.id === id)) return overlays.filter(o => o.id !== id)
  return [...overlays, { id, tint }].slice(-MAX_OVERLAYS)
}

/** Overlays from a list that may hold more than the most there can be (a Khipu step): the first ones, as written. */
export function capOverlays<T>(overlays: readonly T[]): T[] {
  return overlays.slice(0, MAX_OVERLAYS)
}

/** What the defaults depend on. */
export interface DatasetLayerFacts {
  /**
   * A streamed dataset (a DASH row from the stream host). Only these get
   * the automatic borders: a catalog picture or a plain video never does,
   * as in the desktop apps, where local files and images never do.
   */
  readonly streamed: boolean
  /** Frames carry transparency: an alpha stream or a value-encoded palette. */
  readonly transparent: boolean
  /** Fraction (0..1) of the first frame that hides the Earth, or null when unmeasured. */
  readonly coverage: number | null
  /**
   * Nothing is placed automatically: the real-time Sun streams and any
   * other non-Earth body (owner's rule, 2026-10-02). Wins over the rest.
   */
  readonly autoLayersOff?: boolean
  /**
   * AYNI (owner, 2026-10-06): the stream's descriptor asks for the shared
   * country borders over its data (a visible borders layer), and the app
   * honours it even though the frames are transparent. main.ts sets it for
   * every streamed dataset whose descriptor declares them (first only the
   * classified Köppen-Geiger maps; all of them since the owner's second
   * answer the same day).
   */
  readonly declaredBorders?: boolean
  /**
   * AYNI (2026-10-19): the frame covers a box, not the Earth (a regional
   * release, `valueEncoding.geo`). Its coverage is measured over the box, so
   * a full box is not "the whole Earth with colour", and the global borders
   * picture (about 10 km a pixel) only smears over a city: Barcelona's
   * aerial photos came out about 7 times too dark under its coastline. No
   * automatic borders, unless the descriptor declares them.
   */
  readonly regional?: boolean
}

/** From this measured coverage the data has no transparency (0.1 % tolerated), and borders go on top. */
export const FULL_COVER = 0.999

/**
 * The borders overlay to use, best first: "Country Borders (black)", the
 * one the owner's rule names and the desktop apps tick, then any other
 * country borders, then the NASA reference features (borders among
 * them), then coastlines.
 */
function pickBorders(layers: readonly CatalogLayer[]): CatalogLayer | null {
  const overlays = layers.filter(l => l.kind === 'overlay')
  const text = (l: CatalogLayer) => `${l.id} ${l.title}`.toLowerCase()
  return overlays.find(l => /country[\s_-]*borders?/.test(text(l)) && /black/.test(text(l)))
    ?? overlays.find(l => /country[\s_-]*borders?/.test(text(l)))
    ?? overlays.find(l => l.id === 'builtin-nasa-reference-features')
    ?? overlays.find(l => /coastline/.test(text(l)))
    ?? null
}

/**
 * A layer published in a colour keeps it; line art without one is drawn
 * white. The catalog can say so itself (`tint`): the NOAA overlays, whose
 * labels have outlines and whose rivers are blue, keep their own colours.
 */
export function tintFor(layer: CatalogLayer): MapLayerTint {
  if (layer.tint) return layer.tint
  return /\b(white|black)\b|_(white|black)\b/i.test(`${layer.id} ${layer.title}`) ? 'source' : 'white'
}

/** The layers a dataset starts with. */
export function defaultLayers(layers: readonly CatalogLayer[], facts: DatasetLayerFacts): LayerSelection {
  if (facts.autoLayersOff) return NO_LAYERS
  // An opaque picture has no transparency by definition; a transparent
  // one only when its frames say so. Unmeasured transparency gets no
  // borders: better a missing aid than lines scribbled over a sparse map.
  const hidesEarth = !facts.regional && (!facts.transparent || (facts.coverage !== null && facts.coverage >= FULL_COVER))
  const borders = facts.streamed && (hidesEarth || facts.declaredBorders) ? pickBorders(layers) : null
  return { overlays: borders ? [{ id: borders.id, tint: tintFor(borders) }] : [] }
}

/** A viewer's standing choice about the borders overlay. */
export type BordersChoice = 'on' | 'off'

/**
 * What a viewer's change to the layers says about borders: switched on,
 * switched off, or nothing about them.
 */
export function bordersChoice(
  before: LayerSelection,
  after: LayerSelection,
  layers: readonly CatalogLayer[],
): BordersChoice | null {
  const borders = pickBorders(layers)
  if (!borders) return null
  const had = before.overlays.some(o => o.id === borders.id)
  const has = after.overlays.some(o => o.id === borders.id)
  return had === has ? null : has ? 'on' : 'off'
}

/**
 * A dataset's defaults with the viewer's choice about borders kept: only
 * the automatic switch-on is undone by the next dataset, never what the
 * viewer asked for.
 */
export function withBordersChoice(
  selection: LayerSelection,
  choice: BordersChoice | null,
  layers: readonly CatalogLayer[],
): LayerSelection {
  const borders = choice ? pickBorders(layers) : null
  if (!borders) return selection
  const others = selection.overlays.filter(o => o.id !== borders.id)
  return { overlays: choice === 'on' ? [...others, { id: borders.id, tint: tintFor(borders) }] : others }
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

const SAMPLE_W = 64
const SAMPLE_H = 32

/**
 * Fraction of a frame that hides the Earth: pixels with alpha above one
 * half for an alpha stream, or — for a value-encoded frame, whose alpha
 * is always opaque — codes the palette draws at least half opaque (a
 * palette can make low values transparent too, so "has a value" is not
 * "covers the Earth"). Sampled on a 64x32
 * copy, which is plenty for "most of the sphere". Null when the frame
 * cannot be read (not decoded yet, or a cross-origin taint).
 */
export function measureCoverage(
  source: CanvasImageSource & { readonly width?: number },
  mode: { kind: 'alpha'; colorKey?: ColorKey } | { kind: 'palette'; lut: Uint8Array; crop?: { x: number; y: number; width: number; height: number } },
): number | null {
  try {
    const canvas = document.createElement('canvas')
    canvas.width = SAMPLE_W
    canvas.height = SAMPLE_H
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    if (mode.kind === 'palette' && mode.crop) {
      const c = mode.crop
      ctx.drawImage(source, c.x, c.y, c.width, c.height, 0, 0, SAMPLE_W, SAMPLE_H)
    } else {
      ctx.drawImage(source, 0, 0, SAMPLE_W, SAMPLE_H)
    }
    const px = ctx.getImageData(0, 0, SAMPLE_W, SAMPLE_H).data
    return coverageOfPixels(px, mode.kind === 'palette' ? mode.lut : null, mode.kind === 'alpha' ? mode.colorKey : undefined)
  } catch {
    return null
  }
}

/** The counting half of {@link measureCoverage}, separate so it can be tested without a canvas. */
export function coverageOfPixels(rgba: Uint8ClampedArray | Uint8Array, lut: Uint8Array | null, colorKey?: ColorKey): number {
  let covered = 0
  const n = rgba.length / 4
  for (let i = 0; i < n; i++) {
    const o = i * 4
    if (lut ? lut[rgba[o]! * 4 + 3]! > 127 : colorKey ? rgba[o + 3]! * colorKeyAlpha(rgba[o]!, rgba[o + 1]!, rgba[o + 2]!, colorKey) > 127 : rgba[o + 3]! > 127) covered++
  }
  return n ? covered / n : 0
}

// ---------------------------------------------------------------------------
// Catalog + images
// ---------------------------------------------------------------------------

let catalogPromise: Promise<CatalogLayer[]> | null = null

/** The id of the borders overlay the stream host shares (`streamHostLayers`). */
export const STREAM_BORDERS_ID = 'stream-country-borders-black'

/**
 * The overlays the stream host publishes beside its streams, for every
 * install that has the host configured: the "Country Borders (black)"
 * the owner's rule names, the same 4096×2048 picture the desktop apps
 * draw, served immutable and CORS-open at
 * `global/projection/shared/country_borders_black_v1.png`. Listed
 * whether or not the layer catalog answers, so a stream with no
 * transparency gets its borders even where `/api/layers` is down.
 */
export function streamHostLayers(base: string | undefined = streamHostBase()): CatalogLayer[] {
  const root = base?.trim()
  if (!root) return []
  return [{
    id: STREAM_BORDERS_ID,
    title: 'Country Borders (black)',
    kind: 'overlay',
    url: new URL('global/projection/shared/country_borders_black_v1.png', root.endsWith('/') ? root : `${root}/`).toString(),
  }]
}

/**
 * AYNI's own country borders (scripts/overlays/globe-borders): the same
 * lines as the stream host's, drawn the same width on the ground at every
 * latitude, where the stream host's are the same width in pixels and look
 * stretched and broken near the poles. When the catalog lists them, the
 * stream host's layer is drawn from them under its own id, so the
 * defaults, lessons and Khipus that name it keep working and the picker
 * shows one borders layer, not two.
 */
export const EVEN_BORDERS_ID = 'ayni-country-borders'

/** The catalog's and the stream host's layers as one list, with {@link EVEN_BORDERS_ID} folded into the stream host's borders. */
export function mergeLayerLists(fromCatalog: readonly CatalogLayer[], fromHost: readonly CatalogLayer[]): CatalogLayer[] {
  const even = fromCatalog.find(l => l.id === EVEN_BORDERS_ID)
  if (!even || !fromHost.some(l => l.id === STREAM_BORDERS_ID)) return [...fromCatalog, ...fromHost]
  return [
    ...fromCatalog.filter(l => l !== even),
    ...fromHost.map(l => l.id === STREAM_BORDERS_ID ? { ...l, url: even.url } : l),
  ]
}

/**
 * The catalog's overlays plus the stream host's, fetched once per session;
 * only the latter when the catalog is unavailable. The catalog's basemaps
 * are left out: the globe's own Earth is the only one (owner, 2026-10-12).
 */
export function fetchLayerCatalog(fetchImpl: typeof fetch = fetch): Promise<CatalogLayer[]> {
  catalogPromise ??= (async () => {
    let fromCatalog: CatalogLayer[] = []
    try {
      const res = await fetchImpl('/api/layers', { headers: { Accept: 'application/json' } })
      if (res.ok) {
        const body = await res.json() as { layers?: CatalogLayer[] }
        if (Array.isArray(body.layers)) {
          fromCatalog = body.layers.filter(l => l && typeof l.id === 'string' && typeof l.url === 'string' && l.kind === 'overlay')
        }
      }
    } catch { /* the stream host's layers still stand */ }
    return mergeLayerLists(fromCatalog, streamHostLayers())
  })()
  return catalogPromise
}

/** For tests. */
export function resetLayerCatalog(): void {
  catalogPromise = null
  images.clear()
}

const images = new Map<string, Promise<HTMLImageElement | null>>()

/** A layer image, decoded once and shared by every globe; null if it fails to load. */
export function loadLayerImage(url: string): Promise<HTMLImageElement | null> {
  let pending = images.get(url)
  if (!pending) {
    pending = new Promise(resolve => {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.decoding = 'async'
      img.onload = () => resolve(img)
      img.onerror = () => resolve(null)
      img.src = url
    })
    images.set(url, pending)
  }
  return pending
}

/**
 * The images for a selection, in stack order, for a renderer's
 * `setMapLayers`. Layers that are unknown or fail to load are left out.
 */
export async function resolveLayerImages(
  layers: readonly CatalogLayer[],
  selection: LayerSelection,
): Promise<{ overlays: Array<{ image: HTMLImageElement; tint: MapLayerTint }> }> {
  const byId = new Map(layers.map(l => [l.id, l]))
  const overlayImages = await Promise.all(selection.overlays.map(o => {
    const layer = byId.get(o.id)
    return layer ? loadLayerImage(layer.url) : Promise.resolve(null)
  }))
  const overlays: Array<{ image: HTMLImageElement; tint: MapLayerTint }> = []
  selection.overlays.forEach((o, i) => {
    const image = overlayImages[i]
    if (image) overlays.push({ image, tint: o.tint })
  })
  return { overlays }
}
